import classNames from 'classnames';
import React, {
	useMemo,
	useEffect,
	useState,
	useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { ConnectedUser } from '../../connection/socket';
import { Profile } from '../../stores/reducers/invitations';
import { MediaItem } from '../../stores/reducers/media';
import { Note } from '../../stores/reducers/notes';
import { GreenroomState as GRS } from '../../stores/types/types';
import { TeleprompterInterface } from '../../types/interfaces';
import { capitalize, listToArray, uniqueArray } from '../../utils/utils';
import Avatar from './avatar';
import Icon, { COLORS, ICONS } from './icon';
import { SHARE_TYPE } from './share-with';
import useProfile from '../../hooks/use-profile';

interface Props {
	shareListOpen: boolean;
	shareListHidden: boolean;
	closeShareList: (currentlySelected?: number[]) => void;
	selectedItem: MediaItem | TeleprompterInterface | Note | null | undefined;
	creatorId: number | undefined;
	shareType: string;
	constrainToParent?: boolean;
	forMediaDrawer?: boolean;
}

export default function ShareList(props: Props) {
	const {
		shareListOpen,
		shareListHidden,
		closeShareList,
		selectedItem,
		creatorId,
		shareType,
		constrainToParent = false,
		forMediaDrawer = false,
	} = props;

	const connectedUsers = useSelector((s: GRS) => s.SessionReducer.connectedUsers);
	const invitations = useSelector((s: GRS) => s.InvitationsReducer.invitations);
	const user = useSelector((s: GRS) => s.SessionReducer.user);
	const useMasks = useSelector((s: GRS) => s.SettingsReducer.featureFlags.use_profile_masks);

	const [visibleUsers, setVisibleUsers] = useState<ConnectedUser[]>([]);
	const [currentlySelected, setCurrentlySelected] = useState<number[]>([]);
	const [selectAll, setSelectAll] = useState<boolean>(false);

	const sharePopoverElement = useRef<HTMLDivElement | null>(null);

	const handleClick = (e: any) => {
		// Close share with popover if user clicked outside of it
		const path = e.composedPath();
		const len = path.length;
		let keepOpen = false;

		for (let i = 0; i < len; i += 1) {
			if (typeof path[i].className === 'string' && path[i].className.includes('share-popover')) {
				keepOpen = true;
				break;
			}
		}

		if (!keepOpen) {
			closeShareList();
		}
	};

	useEffect(() => {
		if (selectedItem && selectedItem.shared_with) {
			const sharedWithArr = typeof selectedItem.shared_with === 'string' ? listToArray(selectedItem.shared_with) : selectedItem.shared_with;
			setCurrentlySelected(sharedWithArr);
		}
	}, [shareListOpen]);

	useEffect(() => {
		let visUsers = uniqueArray('gr_user', [
			...invitations.map((invite) => invite.profile),
			...connectedUsers,
		].filter((_user: ConnectedUser | Profile) => _user.gr_user !== creatorId));

		// Decoupling migration strategy: include pre-decoupling invites
		// who only have "brandlive_profile" and not "gr_user" yet
		const preDecouplingInvites = invitations.filter(
			(invitation) => !invitation.profile.gr_user && invitation.profile.brandlive_profile,
		);

		visUsers = [...visUsers, ...uniqueArray('brandlive_profile', [
			...preDecouplingInvites.map((invite) => invite.profile),
		].filter((_user: ConnectedUser | Profile) => _user.brandlive_profile !== creatorId))];

		visUsers = visUsers.filter((_user: ConnectedUser) => !_user.isProducer);

		setVisibleUsers(visUsers);
	}, [connectedUsers, invitations, user]);

	useEffect(() => {
		if (shareListOpen && !forMediaDrawer) {
			window.addEventListener('click', handleClick);
		}

		return () => {
			if (!forMediaDrawer) {
				window.removeEventListener('click', handleClick);
			}
		};
	}, [shareListOpen]);

	const search = (e: React.ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value.toLowerCase().replace(/ /g, '');
		if (val) {
			const visUsers = uniqueArray(
				'gr_user',
				[
					...invitations.map((invite) => invite.profile),
					...connectedUsers,
				].filter((_user: ConnectedUser | Profile) => _user.gr_user !== creatorId),
			).filter((_user: ConnectedUser) => {
				const str = (_user.first_name + _user.last_name + _user.email).toLowerCase().replace(/ /g, '');
				return str.includes(val);
			});

			setVisibleUsers(visUsers);
		} else {
			const visUsers = uniqueArray('gr_user', [
				...invitations.map((invite) => invite.profile),
				...connectedUsers,
			].filter((_user: ConnectedUser | Profile) => _user.gr_user !== creatorId));

			setVisibleUsers(visUsers);
		}
	};

	const toggleSelectedUser = (_user: ConnectedUser) => {
		setSelectAll(false);

		// Decoupling migration strategy: include "brandlive_profile" here
		// because pre-decoupled "shared with" data could contain
		// of "brandlive_profile" IDs before "gr_user" IDs existed.
		if (
			currentlySelected.includes(_user.gr_user)
			|| (_user.brandlive_profile && currentlySelected.includes(_user.brandlive_profile))
		) {
			setCurrentlySelected(
				currentlySelected!.filter(
					(prof: number) => prof !== _user.gr_user && prof !== _user.brandlive_profile,
				),
			);
		} else {
			// However, when we select a user, we always use gr_user
			// moving forward if that user has the gr_user field filled out
			// (i.e. They've logged into NG/decoupling before).
			// If they are a user who was invited pre-decoupling and hasn't logged in
			// yet we still select them with "brandlive_profile" because they don't have "gr_user"
			setCurrentlySelected([
				...currentlySelected!, _user.gr_user ? _user.gr_user : _user.brandlive_profile!,
			]);
		}
	};

	const toggleSelectAll = () => {
		if (selectAll) {
			setCurrentlySelected([]);
			setSelectAll(false);
		} else {
			// Decoupling migration strategy: pre-decoupling shared with users
			// used "brandlive_profile" before "gr_user" existed
			const newSelected = visibleUsers.map(
				(_user: ConnectedUser) => (_user.gr_user ? _user.gr_user : _user.brandlive_profile!),
			);
			setCurrentlySelected(newSelected);
			setSelectAll(true);
		}
	};

	const getShareText = () => {
		switch (shareType) {
			case SHARE_TYPE.MEDIA:
			case SHARE_TYPE.SLIDE_NOTE:
				return 'Presentation Controls';
			case SHARE_TYPE.SHOT_NOTE:
				return 'Shot Note';
			default:
				return capitalize(shareType);
		}
	};

	const setShare = () => {
		closeShareList(currentlySelected);
		setCurrentlySelected([]);
	};

	const isSharePopoverOffScreen = useMemo((): boolean => {
		// adding offscreen logic to the notes or teleprompter popover
		// was causing issues but not with media
		if (shareType === SHARE_TYPE.NOTE || shareType === SHARE_TYPE.SHOT_NOTE) {
			return false;
		}

		if (constrainToParent && shareListOpen && sharePopoverElement.current) {
			const sharePopRect = sharePopoverElement.current.getBoundingClientRect();
			return sharePopRect.bottom > (window.innerHeight || document.documentElement.clientHeight);
		}

		return false;
	}, [constrainToParent, shareListOpen]);

	const {getMaskedProfileById} = useProfile({})

	return (
		<div className={classNames('share-popover', { open: shareListOpen, hidden: shareListHidden, 'off-screen': isSharePopoverOffScreen })} ref={sharePopoverElement}>
			<div className="flex margin-top-medium">
				{forMediaDrawer && (
					<div className="align-items-center margin-right-medium cursor-pointer" onClick={() => closeShareList()}>
						<Icon name={ICONS.ARROW_LEFT} size={16} color={COLORS.WHITE} />
					</div>
				)}
				<div className="search-bar">
					<Icon name={ICONS.SEARCH} color={COLORS.GRAY} size={17} />
					<input placeholder="Search for name" type="text" onChange={search} />
				</div>
			</div>
			<div className="share-people-header">
				<label>People</label>
			</div>
			<div className="share-people-container">
				<div className="share-people">
					<div className="share-person" onClick={toggleSelectAll}>
						<div className="icon-wrapper">
							<Icon size={20} name={ICONS.PEOPLE} color={COLORS.WHITE} />
						</div>
						<div className="share-person-metadata">
							<p>Select All</p>
							<span>{visibleUsers.length} {visibleUsers.length === 1 ? 'Person' : 'People'}</span>
						</div>
						<div className={classNames('share-checkbox', { selected: selectAll })}>
							<div className="checkbox-outer">
								<Icon name={ICONS.CHECK} size={9} color={COLORS.WHITE} />
							</div>
						</div>
					</div>
					{visibleUsers.map((_user: ConnectedUser) => {
						const mask = getMaskedProfileById(_user.gr_user)
						const profile = (useMasks && mask) ? mask : _user;
						return <div key={_user.gr_user} className="share-person" onClick={() => toggleSelectedUser(_user)}>
							<Avatar size={'thirtytwo'} noBorder user={_user} />
							<div className="share-person-metadata">
								<p>{profile.first_name} {profile.last_name}</p>
								<span className="truncate-overflow">{_user.email}</span>
							</div>
							{/* Decoupling migration strategy: include "brandlive_profile"
							// here because pre-decoupled "shared with" data could contain
							// "brandlive_profile" IDs and not "gr_user" IDs. */}
							<div className={classNames('share-checkbox', { selected: currentlySelected.includes(_user.gr_user) || (_user.brandlive_profile && currentlySelected.includes(_user.brandlive_profile)) })}>
								<div className="checkbox-outer">
									<Icon name={ICONS.CHECK} size={9} color={COLORS.WHITE} />
								</div>
							</div>
						</div>
					})}
				</div>
			</div>
			<button
				className={classNames('share-note', forMediaDrawer && 'media-drawer')}
				onClick={setShare}
				disabled={currentlySelected.length === 0}
			>
				<Icon name={ICONS.SHARE} size={17} color={forMediaDrawer ? COLORS.BLACK : COLORS.WHITE} />
				Share {getShareText()}
			</button>
		</div>
	);
}
